import { useCallback, useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowLeft, Shield, Users, Landmark, Send, FileSignature, RefreshCw, Lock, Download, History, Pencil, Eye } from "lucide-react";

const API = "/api"; // same-origin — the ingress proxies /api to the backend
const GOLD = "#eec643";

const card = "rounded-xl border border-stone-700 bg-stone-900/60 p-5";
const label = "text-[10px] font-mono uppercase tracking-widest text-amber-500/80 mb-3";
const input = "bg-stone-950 border border-stone-700 rounded-lg px-4 py-2.5 text-stone-100 font-mono text-sm focus:border-amber-500/60 outline-none";
const btn = "px-5 py-2.5 rounded-full font-semibold text-sm text-stone-950 transition-transform hover:scale-[1.03] active:scale-95 disabled:opacity-50";
const KIND_DOTS = {
  EXCHANGE: "bg-amber-400", VISIT: "bg-emerald-400", CALL: "bg-sky-400",
  APPOINTMENT: "bg-rose-400", SCHOOL: "bg-violet-400", MEDICAL: "bg-red-400", OTHER: "bg-stone-400",
};

const CovenantCalendar = ({ token, myUuid }) => {
  const [cursor, setCursor] = useState(() => { const d = new Date(); d.setDate(1); return d; });
  const [events, setEvents] = useState([]);
  const [kinds, setKinds] = useState(Object.keys(KIND_DOTS));
  const [linked, setLinked] = useState(true);
  const [selected, setSelected] = useState(() => new Date().toISOString().slice(0, 10));
  const [form, setForm] = useState({ title: "", kind: "EXCHANGE", starts_at: "", note: "" });
  const [flashLocal, setFlashLocal] = useState("");

  const monthKey = `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, "0")}`;
  const loadEvents = useCallback(async () => {
    const res = await fetch(`${API}/wallet/calendar/events?month=${monthKey}`,
      { headers: { Authorization: `Bearer ${token}` } });
    if (res.ok) { const d = await res.json(); setEvents(d.events); setKinds(d.kinds); setLinked(d.linked); }
  }, [token, monthKey]);
  useEffect(() => { loadEvents(); }, [loadEvents]);

  const byDay = {};
  for (const e of events) { (byDay[e.starts_at.slice(0, 10)] ||= []).push(e); }
  const year = cursor.getFullYear(), monthIdx = cursor.getMonth();
  const firstWeekday = new Date(year, monthIdx, 1).getDay();
  const daysInMonth = new Date(year, monthIdx + 1, 0).getDate();
  const todayKey = new Date().toISOString().slice(0, 10);

  const post = async (path, body, okMsg) => {
    setFlashLocal("");
    const res = await fetch(`${API}${path}`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      body: JSON.stringify(body || {}),
    });
    const data = await res.json();
    if (res.ok) { setFlashLocal(okMsg); await loadEvents(); return data; }
    setFlashLocal(typeof data.detail === "string" ? data.detail : "request failed");
    return null;
  };

  const move = (n) => { const d = new Date(cursor); d.setMonth(d.getMonth() + n); setCursor(d); };

  return (
    <div className={card} data-testid="covenant-calendar">
      <p className={label}>shared household calendar — one clock, both parents</p>
      <p className="text-xs text-stone-500 leading-relaxed mb-4">
        Exchanges, visits, calls, appointments — set once, seen by both.
        {linked ? " The co-parent is texted the moment you set an entry, and every set or cancel seals into the conduct ledger as a TIME entry." :
          " Link the covenant to share the clock; everything you set still seals into your conduct ledger."}
      </p>
      <div className="flex items-center justify-between mb-3">
        <button data-testid="cal-prev" onClick={() => move(-1)} className="text-stone-400 hover:text-amber-400 px-2">←</button>
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-stone-300" data-testid="cal-month-label">
          {cursor.toLocaleString(undefined, { month: "long", year: "numeric" })}
        </p>
        <button data-testid="cal-next" onClick={() => move(1)} className="text-stone-400 hover:text-amber-400 px-2">→</button>
      </div>
      <div className="grid grid-cols-7 gap-1 text-center font-mono text-[10px] text-stone-500 mb-1">
        {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => <span key={i}>{d}</span>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {Array.from({ length: firstWeekday }).map((_, i) => <span key={`b${i}`} />)}
        {Array.from({ length: daysInMonth }).map((_, i) => {
          const day = i + 1;
          const key = `${monthKey}-${String(day).padStart(2, "0")}`;
          const dayEvents = byDay[key] || [];
          const isSelected = key === selected;
          return (
            <button key={day} data-testid={`cal-day-${key}`} onClick={() => setSelected(key)}
              className={`rounded-md py-1.5 text-xs font-mono transition-colors ${isSelected ? "bg-amber-500/20 text-amber-300" : "text-stone-400 hover:bg-stone-800"} ${key === todayKey ? "ring-1 ring-amber-500/60" : ""}`}>
              {day}
              <span className="flex justify-center gap-0.5 mt-0.5 h-1">
                {dayEvents.slice(0, 3).map((e) => (
                  <span key={e.event_id} className={`w-1 h-1 rounded-full ${KIND_DOTS[e.kind] || KIND_DOTS.OTHER} ${e.status === "CANCELLED" ? "opacity-30" : ""}`} />
                ))}
              </span>
            </button>
          );
        })}
      </div>

      <div className="mt-4 border-t border-stone-800/60 pt-3" data-testid="cal-day-events">
        <p className="font-mono text-[10px] uppercase tracking-widest text-stone-500 mb-2">{selected}</p>
        {(byDay[selected] || []).length === 0 && <p className="text-xs text-stone-600">Nothing set for this day.</p>}
        {(byDay[selected] || []).map((e) => (
          <div key={e.event_id} data-testid={`cal-event-${e.event_id}`}
            className={`flex items-center gap-2 py-1.5 border-b border-stone-800/60 ${e.status === "CANCELLED" ? "opacity-40 line-through" : ""}`}>
            <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${KIND_DOTS[e.kind] || KIND_DOTS.OTHER}`} />
            <span className="font-mono text-[10px] text-stone-500 w-12 shrink-0">{(e.starts_at.slice(11, 16)) || ""}</span>
            <span className="text-xs text-stone-200 flex-1">{e.title}</span>
            <span className="font-mono text-[9px] text-stone-600">{e.creator_label}</span>
            {e.status === "SET" && e.creator_uuid !== myUuid && !(e.acknowledged_by || []).includes(myUuid) && (
              <button data-testid={`cal-ack-${e.event_id}`}
                onClick={() => post(`/wallet/calendar/events/${e.event_id}/ack`, {}, "Acknowledged — both parents on the same page.")}
                className="px-2.5 py-1 rounded-full text-[10px] font-semibold border border-emerald-500/50 text-emerald-300 hover:bg-emerald-500/10">
                ACK
              </button>
            )}
            {e.status === "SET" && e.creator_uuid === myUuid && (
              <button data-testid={`cal-cancel-${e.event_id}`}
                onClick={() => post(`/wallet/calendar/events/${e.event_id}/cancel`, {}, "Cancelled — the cancellation seals into the ledger too.")}
                className="px-2.5 py-1 rounded-full text-[10px] font-semibold border border-stone-600 text-stone-400 hover:border-red-500/60 hover:text-red-300">
                CANCEL
              </button>
            )}
            {e.status === "SET" && (e.acknowledged_by || []).length >= 2 && (
              <span className="font-mono text-[9px] text-emerald-500/80">BOTH SEEN</span>
            )}
          </div>
        ))}
      </div>

      <div className="mt-4 border-t border-stone-800/60 pt-3">
        <div className="flex gap-2 flex-wrap mb-2">
          <select data-testid="cal-form-kind" value={form.kind}
            onChange={(e) => setForm({ ...form, kind: e.target.value })} className={input}>
            {kinds.map((k) => <option key={k} value={k}>{k}</option>)}
          </select>
          <input data-testid="cal-form-start" type="datetime-local" value={form.starts_at}
            onChange={(e) => setForm({ ...form, starts_at: e.target.value })} className={input} />
        </div>
        <div className="flex gap-2 flex-wrap">
          <input data-testid="cal-form-title" placeholder="what it is — e.g. custody exchange at the school gate"
            value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
            className={`${input} flex-1 min-w-[220px]`} />
          <button data-testid="cal-add-button" disabled={!form.title.trim() || !form.starts_at}
            onClick={async () => {
              const r = await post("/wallet/calendar/events", form, "Set — the co-parent is texted, and it seals into the ledger.");
              if (r) setForm({ title: "", kind: "EXCHANGE", starts_at: "", note: "" });
            }} className={btn} style={{ background: GOLD }}>SET</button>
        </div>
        {flashLocal && <p data-testid="cal-flash" className="text-xs text-amber-400 mt-2">{flashLocal}</p>}
      </div>
    </div>
  );
};

const FieldLogCard = ({ token }) => {
  const [vector, setVector] = useState("DOORSTEP");
  const [agent, setAgent] = useState("");
  const [notes, setNotes] = useState("");
  const [flashLocal, setFlashLocal] = useState("");
  const [busyLocal, setBusyLocal] = useState(false);

  const logIt = async () => {
    setBusyLocal(true); setFlashLocal("");
    try {
      const res = await fetch(`${API}/wallet/field-log`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ vector, agent_identity: agent, notes }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "request failed");
      setFlashLocal(`Sealed as ledger entry #${data.entry.seq} — the council was texted (${data.council_notified}).`);
      setAgent(""); setNotes("");
    } catch (e) { setFlashLocal(e.message); } finally { setBusyLocal(false); }
  };

  return (
    <div className={card} data-testid="field-log-card">
      <p className={label}>field contact log — doorstep, school, notice: log it where you stand</p>
      <p className="text-xs text-stone-500 leading-relaxed mb-3">
        Someone from an agency made contact? Thirty seconds: what kind, who they said they were,
        what was said. It seals into the conduct ledger instantly and the council is texted.
        Write it as it happened — plain words, no conclusions.
      </p>
      <div className="flex gap-2 flex-wrap mb-2">
        <select data-testid="field-vector" value={vector} onChange={(e) => setVector(e.target.value)} className={input}>
          <option value="DOORSTEP">DOORSTEP</option>
          <option value="SCHOOL">SCHOOL</option>
          <option value="WRITTEN_NOTICE">WRITTEN NOTICE</option>
          <option value="PHONE">PHONE</option>
          <option value="OTHER">OTHER</option>
        </select>
        <input data-testid="field-agent" placeholder="name or badge, as given (optional)"
          value={agent} onChange={(e) => setAgent(e.target.value)} className={`${input} flex-1 min-w-[200px]`} />
      </div>
      <div className="flex gap-2 flex-wrap">
        <input data-testid="field-notes" placeholder="what was said or left — e.g. left a card asking us to call about an assessment"
          value={notes} onChange={(e) => setNotes(e.target.value)} className={`${input} flex-1 min-w-[240px]`} />
        <button data-testid="field-log-button" disabled={busyLocal || !notes.trim()} onClick={logIt}
          className={btn} style={{ background: GOLD }}>LOG + SEAL</button>
      </div>
      {flashLocal && <p data-testid="field-log-flash" className="text-xs text-amber-400 mt-2">{flashLocal}</p>}
    </div>
  );
};

const fmt = (n) => (n == null ? "—" : `$${Number(n).toLocaleString("en-US", { maximumFractionDigits: 2 })}`);
const KIND_COLORS = { INVITE: "bg-amber-400", LINK: "bg-emerald-400", DEPOSIT: "bg-emerald-400", MAINTENANCE: "bg-stone-500", STAGED: "bg-amber-400", RELEASED: "bg-emerald-400", SEALED: "bg-stone-500", SIGNED: "bg-emerald-400" };

export default function CovenantWallet() {
  const [token, setToken] = useState(() => localStorage.getItem("covenant_token") || "");
  const [login, setLogin] = useState({ ledger_uuid: new URLSearchParams(window.location.search).get("uuid") || "", phone: "" });
  const [me, setMe] = useState(null);
  const [cardInfo, setCardInfo] = useState(null);
  const [view, setView] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState("");
  const [maintainForm, setMaintainForm] = useState({ amount: "", asset: "SUSD" });
  const [poolForm, setPoolForm] = useState({ amount: "" });
  const [proposalForm, setProposalForm] = useState({ amount: "", purpose: "", recipient: "", category: "GENERAL" });
  const [sealingStatement, setSealingStatement] = useState(false);
  const [invitePhone, setInvitePhone] = useState("");
  const [vaultDocs, setVaultDocs] = useState([]);
  const [vaultForm, setVaultForm] = useState({ title: "", category: "FILING", deadline: "", text: "", shared: false });
  const [vaultFile, setVaultFile] = useState(null); // {name, b64} — scanned PDFs seal directly
  const fileRef = useRef(null);
  const [editingDoc, setEditingDoc] = useState(null); // doc_id being re-drafted, or null
  const [versionsOpen, setVersionsOpen] = useState({}); // doc_id -> versions array
  const [ledger, setLedger] = useState({ entries: [], chain_head: null, kinds: [] });
  const [ledgerForm, setLedgerForm] = useState({ kind: "NOTE", text: "", amount: "" });

  const exportLedger = async () => {
    try {
      const res = await fetch(`${API}/wallet/court-ledger/export.pdf`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("export failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "BGNZN-conduct-ledger.pdf"; a.click();
      URL.revokeObjectURL(url);
    } catch { setError("The exhibit could not be generated right now."); }
  };

  const toggleVersions = async (docId) => {
    if (versionsOpen[docId]) { setVersionsOpen((v) => ({ ...v, [docId]: null })); return; }
    try {
      const res = await fetch(`${API}/wallet/vault/documents/${docId}/versions`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      if (!res.ok) throw new Error("versions failed");
      setVersionsOpen((v) => ({ ...v, [docId]: data.versions || [] }));
    } catch { setError("Version history could not be loaded."); }
  };

  const downloadVersion = async (docId, index) => {
    try {
      const res = await fetch(`${API}/wallet/vault/documents/${docId}/versions/${index}/download`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("version download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `BGNZN-VAULT-${docId}-v${index + 1}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { setError("That archived version could not be opened."); }
  };

  const startEdit = async (docId) => {
    setBusy(true); setError("");
    try {
      const res = await fetch(`${API}/wallet/vault/documents/${docId}/source`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "source failed");
      setVaultForm({ title: data.title, category: "PROPOSAL", deadline: "", text: data.text_body, shared: true });
      setVaultFile(null);
      setEditingDoc(docId);
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  const pickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 6_000_000) { setError("PDF too large — 6 MB max."); return; }
    const reader = new FileReader();
    reader.onload = () => {
      const b64 = String(reader.result).split(",")[1] || "";
      setVaultFile({ name: f.name, b64 });
      setVaultForm((v) => ({ ...v, title: v.title || f.name.replace(/\.pdf$/i, "") }));
    };
    reader.readAsDataURL(f);
  };

  const loadTemplate = async (id) => {
    setBusy(true); setError("");
    try {
      const res = await fetch(`${API}/wallet/vault/templates/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "template failed");
      setVaultForm({ title: data.title, category: data.category, deadline: "", text: data.text_body, shared: false });
      setFlash("");
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  const downloadDoc = async (docId) => {
    try {
      const res = await fetch(`${API}/wallet/vault/documents/${docId}/download`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `BGNZN-VAULT-${docId}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { setError("Document could not be opened right now."); }
  };

  const downloadStatement = async () => {
    setSealingStatement(true);
    try {
      const res = await fetch(`/api/wallet/covenant/statement`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("statement failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = res.headers.get("Content-Disposition")?.match(/filename=(\S+)/)?.[1] || "BGNZN-HOUSEHOLD.pdf";
      a.click();
      URL.revokeObjectURL(url);
    } catch { setError("Statement could not be sealed right now."); } finally { setSealingStatement(false); }
  };

  const authHeaders = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };

  const load = useCallback(async (tok = token) => {
    if (!tok) return;
    try {
      const [meRes, cardRes, covRes, vaultRes, ledgerRes] = await Promise.all([
        fetch(`${API}/wallet/me`, { headers: { Authorization: `Bearer ${tok}` } }),
        fetch(`${API}/wallet/card`, { headers: { Authorization: `Bearer ${tok}` } }),
        fetch(`${API}/wallet/covenant-tape`, { headers: { Authorization: `Bearer ${tok}` } }),
        fetch(`${API}/wallet/vault/documents`, { headers: { Authorization: `Bearer ${tok}` } }),
        fetch(`${API}/wallet/court-ledger`, { headers: { Authorization: `Bearer ${tok}` } }),
      ]);
      if (meRes.status === 401) {
        localStorage.removeItem("covenant_token");
        setToken("");
        return;
      }
      if (meRes.ok) setMe(await meRes.json());
      if (cardRes.ok) setCardInfo(await cardRes.json());
      if (covRes.ok) setView(await covRes.json());
      if (vaultRes.ok) setVaultDocs((await vaultRes.json()).documents || []);
      if (ledgerRes.ok) setLedger(await ledgerRes.json());
    } catch { /* additive */ }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const signIn = async () => {
    setBusy(true); setError("");
    try {
      const res = await fetch(`${API}/wallet/session`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ledger_uuid: login.ledger_uuid.trim(), phone: login.phone.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "sign-in failed");
      localStorage.setItem("covenant_token", data.token);
      setToken(data.token);
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  const act = async (path, body) => {
    setBusy(true); setFlash(""); setError("");
    try {
      const res = await fetch(`${API}${path}`, { method: "POST", headers: authHeaders, body: JSON.stringify(body) });
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "request failed");
      await load();
      return data;
    } catch (e) { setError(e.message); return null; } finally { setBusy(false); }
  };

  const downloadPresentment = async () => {
    setBusy(true); setError("");
    try {
      const res = await fetch(`${API}/wallet/covenant/presentment.pdf`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("presentment failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "BGNZN-covenant-presentment.pdf"; a.click();
      URL.revokeObjectURL(url);
      setFlash("Presentment downloaded — hand it to her attorneys; it walks them through everything, including how to verify.");
    } catch { setError("The presentment could not be rendered right now."); } finally { setBusy(false); }
  };

  const mintPeek = async () => {
    const ok = await act("/wallet/covenant/peek", {});
    if (ok) setFlash("Her window link is live — text it to her. It shows structure and integrity only, never identifiers.");
  };

  const revokePeek = async () => {
    setBusy(true); setFlash(""); setError("");
    try {
      const res = await fetch(`${API}/wallet/covenant/peek`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("revoke failed");
      await load();
      setFlash("Window link revoked — the old URL now answers nothing.");
    } catch { setError("The window link could not be revoked right now."); } finally { setBusy(false); }
  };

  const peekUrl = view?.peek ? `${window.location.origin}/covenant/peek/${view.peek.token}` : null;

  const peekBlock = (
    <div data-testid="peek-block" className="border border-stone-700/70 rounded-lg p-3 mt-4">
      <p className={label}><Eye size={12} className="inline mr-2 -mt-0.5" />her window in — read-only</p>
      <p className="text-[11px] text-stone-500 leading-relaxed mb-3">
        One link lets her — and her attorneys — see what the covenant is: the structure, the pool,
        the milestones, and the sealed-record count. No balances of yours, no identifiers, no card or
        wallet internals. Rotate or revoke it anytime; every issuance seals into your conduct ledger.
      </p>
      {view?.assent && (
        <p data-testid="assent-badge" className={`font-mono text-[10px] inline-block border rounded-full px-3 py-1 mb-3 ${view.assent.badge === "MUTUAL_ASSENT" ? "text-emerald-300 border-emerald-500/40" : "text-amber-300/90 border-amber-500/30"}`}>
          {view.assent.label}{view.assent.signature_blocks?.length > 0 ? ` · blocks ${view.assent.signature_blocks.join(" + ")}` : ""}{view.assent.title ? ` — ${view.assent.title}` : ""}
        </p>
      )}
      {peekUrl ? (
        <div className="flex gap-2 flex-wrap items-center mb-2">
          <input readOnly data-testid="peek-url" value={peekUrl} onFocus={(e) => e.target.select()}
            className={`${input} flex-1 min-w-[220px] !text-[11px]`} />
          <button data-testid="peek-copy" onClick={() => { navigator.clipboard?.writeText(peekUrl); setFlash("Window link copied — text it to her."); }}
            className="px-4 py-2 rounded-full text-xs font-semibold border border-stone-600 text-stone-200 hover:border-amber-500/60 transition-colors">COPY</button>
          <button data-testid="peek-rotate" onClick={mintPeek} disabled={busy}
            className="px-4 py-2 rounded-full text-xs font-semibold border border-stone-600 text-stone-200 hover:border-amber-500/60 transition-colors">ROTATE</button>
          <button data-testid="peek-revoke" onClick={revokePeek} disabled={busy}
            className="px-4 py-2 rounded-full text-xs font-semibold border border-stone-600 text-red-400/80 hover:border-red-500/60 transition-colors">REVOKE</button>
        </div>
      ) : (
        <button data-testid="peek-mint" onClick={mintPeek} disabled={busy}
          className={`${btn} mb-2`} style={{ background: GOLD }}>CREATE HER WINDOW LINK</button>
      )}
      <div className="flex gap-2 flex-wrap">
        <button data-testid="presentment-download" onClick={downloadPresentment} disabled={busy}
          className="px-4 py-2 rounded-full text-xs font-semibold border border-amber-500/50 text-amber-300 hover:bg-amber-500/10 transition-colors">
          PRESENTMENT PDF FOR HER COUNSEL
        </button>
        <button data-testid="presentment-seal" disabled={busy}
          onClick={async () => {
            const ok = await act("/wallet/covenant/presentment/seal", {});
            if (ok) setFlash(`Presentment sealed into the vault — ${ok.doc_id} · seal ${ok.seal.slice(0, 12)}… ${ok.note}`);
          }}
          className="px-4 py-2 rounded-full text-xs font-semibold border border-stone-600 text-stone-200 hover:border-amber-500/60 transition-colors">
          SEAL PRESENTMENT INTO VAULT
        </button>
      </div>
    </div>
  );

  if (!token || !me) {
    return (
      <div data-testid="covenant-signin" className="min-h-screen bg-stone-950 text-stone-200 flex items-center justify-center px-6">
        <div className={`${card} w-full max-w-sm`}>
          <p className={label}>family covenant · 50/50</p>
          <h1 className="text-2xl font-bold text-stone-100 mb-1" style={{ fontFamily: "Georgia, serif" }}>The Household Wallet</h1>
          <p className="text-xs text-stone-500 mb-5 leading-relaxed">
            Co-parent sign-in. You only ever see your own membership, your own card, your own balance —
            and the shared child's pool you both fund. Nothing else.
          </p>
          <input data-testid="covenant-login-uuid" placeholder="member ledger uuid" value={login.ledger_uuid}
            onChange={(e) => setLogin({ ...login, ledger_uuid: e.target.value })} className={`${input} w-full mb-3`} />
          <input data-testid="covenant-login-phone" placeholder="phone on file" value={login.phone}
            onChange={(e) => setLogin({ ...login, phone: e.target.value })} className={`${input} w-full mb-4`} />
          {error && <p data-testid="covenant-login-error" className="text-red-400 text-xs mb-3">{error}</p>}
          <button data-testid="covenant-login-button" onClick={signIn} disabled={busy} className={`${btn} w-full`} style={{ background: GOLD }}>
            OPEN THE HOUSEHOLD WALLET
          </button>
        </div>
      </div>
    );
  }

  const cov = view?.covenant;
  const myUuid = me.ledger_uuid || "";
  const cardLast4 = cardInfo?.card_number ? String(cardInfo.card_number).slice(-4) : null;

  return (
    <div data-testid="covenant-wallet" className="min-h-screen bg-stone-950 text-stone-200">
      <header className="max-w-3xl mx-auto px-6 pt-8 pb-4 flex items-center justify-between">
        <Link to="/" data-testid="covenant-back-link" className="inline-flex items-center gap-2 text-stone-500 hover:text-amber-400 text-xs uppercase tracking-[0.3em] transition-colors">
          <ArrowLeft size={14} /> the institute
        </Link>
        <button data-testid="covenant-refresh" onClick={() => load()} className="text-stone-500 hover:text-amber-400 transition-colors"><RefreshCw size={16} /></button>
      </header>

      <main className="max-w-3xl mx-auto px-6 pb-24 space-y-5">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className={card}>
          <p className={label}><Users size={12} className="inline mr-2 -mt-0.5" />my side of the covenant</p>
          <div className="flex items-end justify-between flex-wrap gap-3">
            <div>
              <p data-testid="covenant-my-label" className="text-xl font-bold text-stone-100" style={{ fontFamily: "Georgia, serif" }}>{me.label}</p>
              <p data-testid="covenant-my-uuid" className="font-mono text-[11px] text-stone-500">uuid …{myUuid.slice(-8)}</p>
              {cardLast4 && <p data-testid="covenant-my-card" className="font-mono text-[11px] text-stone-500">my metal card •••• {cardLast4} — my custody, my keys</p>}
            </div>
            <div className="text-right">
              <p className="text-[10px] uppercase tracking-widest text-stone-500">my wallet</p>
              <p data-testid="covenant-my-balance" className="font-mono text-2xl text-amber-400">{fmt(me.susd_balance)}</p>
            </div>
          </div>
        </motion.div>

        {!cov ? (
          <div data-testid="covenant-none" className={card}>
            <p className={label}><Users size={12} className="inline mr-2 -mt-0.5" />link your co-parent</p>
            <p className="text-sm text-stone-400 leading-relaxed mb-4">
              No family covenant is linked to your membership yet. Enter your co-parent's phone number and
              they get a text — their reply <span className="text-stone-200 font-semibold">YES</span> links
              the covenant. From then on this wallet runs the 50/50 household rail: maintenance, the child's
              pool, and 2-of-2 disbursements.
            </p>
            {view?.pending_invite && (
              <p data-testid="invite-pending-line" className="font-mono text-[11px] text-amber-400/90 border border-amber-500/30 rounded-lg px-3 py-2 mb-4">
                INVITE {view.pending_invite.invite_id} PENDING — sent {new Date(view.pending_invite.sent_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                {view.pending_invite.awaiting_registration ? " · accepted; links itself when their wallet opens" : " · awaiting their YES"}
              </p>
            )}
            <div className="flex gap-2 flex-wrap">
              <input data-testid="invite-phone" placeholder="co-parent phone — 7025551234" value={invitePhone}
                onChange={(e) => setInvitePhone(e.target.value)} className={`${input} flex-1 min-w-[200px]`} />
              <button data-testid="invite-send-button" disabled={busy}
                onClick={async () => {
                  const ok = await act("/wallet/covenant/invite", { phone: invitePhone });
                  if (ok) { setFlash(ok.message); setInvitePhone(""); }
                }} className={btn} style={{ background: GOLD }}>INVITE BY TEXT</button>
            </div>
            <p className="text-[10px] text-stone-500 mt-3">Same rail by SMS: text /INVITE {invitePhone || "7025551234"} — their YES is consent and acceptance in one reply. No wallet yet on their side? The covenant links itself the moment their wallet opens.</p>
            {peekBlock}
          </div>
        ) : (
          <>
            <div className={card}>
              <p className={label}><Shield size={12} className="inline mr-2 -mt-0.5" />the covenant</p>
              <div className="flex justify-between items-center flex-wrap gap-2">
                <p data-testid="covenant-id" className="font-mono text-sm text-amber-300">{cov.covenant_id}</p>
                <p className="text-xs text-stone-400">with <span data-testid="covenant-partner" className="text-stone-200">{cov.partner_label || "co-parent"}</span></p>
                <p className="text-[11px] text-stone-500">split {cov.allocation_split_pct}/{100 - cov.allocation_split_pct}{cov.routing_locked ? " · ROUTING LOCKED" : ""}</p>
                <button data-testid="statement-download" onClick={downloadStatement} disabled={sealingStatement}
                  className="px-4 py-1.5 rounded-full text-xs font-semibold border border-stone-600 text-stone-200 hover:border-amber-500/60 transition-colors">
                  {sealingStatement ? "SEALING…" : "MONTHLY STATEMENT PDF"}
                </button>
              </div>
              {Object.keys(view?.milestones || {}).length > 0 && (
                <div data-testid="milestone-chips" className="flex flex-wrap gap-2 mt-3">
                  {Object.entries(view.milestones).map(([cat, amt]) => (
                    <span key={cat} className="font-mono text-[10px] text-emerald-300 border border-emerald-500/30 rounded-full px-3 py-1">
                      {cat} · {fmt(amt)}
                    </span>
                  ))}
                </div>
              )}
              {peekBlock}
            </div>

            <div className={card}>
              <p className={label}><Landmark size={12} className="inline mr-2 -mt-0.5" />the child's pool — funded by real deposits only</p>
              <div className="flex items-end justify-between flex-wrap gap-3 mb-3">
                <p data-testid="covenant-pool-balance" className="font-mono text-3xl text-emerald-400">{fmt(view?.pool?.available_equity_usd)}</p>
                <p className="text-[10px] text-stone-500 max-w-[240px]">escrow {cov.escrow_address ? `…${cov.escrow_address.slice(-8)}` : "not deployed yet"} · 2-of-2 signatures to release</p>
              </div>
              {(view?.pool?.funding_history || []).map((h, i) => (
                <div key={i} data-testid="covenant-funding-row" className="flex justify-between font-mono text-[11px] py-1 border-b border-stone-800/60">
                  <span className="text-emerald-400">+{fmt(h.amount_usd)} from {h.source}</span>
                  <span className="text-stone-500">block {h.block}</span>
                </div>
              ))}
              {cov.escrow_address && (
                <div className="flex gap-2 mt-3">
                  <input data-testid="pool-amount" placeholder="Deposit USD" value={poolForm.amount}
                    onChange={(e) => setPoolForm({ amount: e.target.value })} className={`${input} w-36`} />
                  <button data-testid="pool-contribute-button" disabled={busy}
                    onClick={async () => {
                      const ok = await act("/wallet/covenant/contribute", { amount_usd: Number(poolForm.amount) || 0 });
                      if (ok) { setFlash(`Pool funded — ${fmt(ok.amount_usd)} sealed at block ${ok.block}.`); setPoolForm({ amount: "" }); }
                    }} className={btn} style={{ background: GOLD }}>CONTRIBUTE FROM MY WALLET</button>
                </div>
              )}
            </div>

            <div className={card}>
              <p className={label}><Send size={12} className="inline mr-2 -mt-0.5" />maintenance — direct, T+0</p>
              <div className="flex gap-2 flex-wrap mb-3">
                <input data-testid="maintain-amount" placeholder="Amount USD" value={maintainForm.amount}
                  onChange={(e) => setMaintainForm({ ...maintainForm, amount: e.target.value })} className={`${input} w-36`} />
                <select data-testid="maintain-asset" value={maintainForm.asset}
                  onChange={(e) => setMaintainForm({ ...maintainForm, asset: e.target.value })} className={input}>
                  {["SUSD", "AU", "AG", "PT", "CU"].map((a) => <option key={a} value={a}>{a}</option>)}
                </select>
                <button data-testid="maintain-send-button" disabled={busy}
                  onClick={async () => {
                    const ok = await act("/wallet/covenant/maintain", { amount_usd: Number(maintainForm.amount) || 0, asset_label: maintainForm.asset });
                    if (ok) {
                      setFlash(`Maintenance cleared — ${fmt(ok.amount_usd)} sealed at block ${ok.block}${ok.metal_equivalent_grams ? ` (≈${ok.metal_equivalent_grams}g ${ok.asset_label})` : ""}.`);
                      setMaintainForm({ amount: "", asset: "SUSD" });
                    }
                  }} className={btn} style={{ background: GOLD }}>SEND MAINTENANCE</button>
              </div>
              <p className="text-[10px] text-stone-500 mb-2">Same rail by text: /MAINTAIN {maintainForm.amount || "250"} {maintainForm.asset} — straight to {cov.partner_label || "co-parent"}, block-numbered, no third-party ledger.</p>
              {(view?.entries || []).map((e, i) => (
                <div key={i} data-testid="maintain-tape-row" className="flex justify-between font-mono text-[11px] py-1 border-b border-stone-800/60">
                  <span className={e.direction === "SENT" ? "text-stone-400" : "text-emerald-400"}>
                    {e.direction === "SENT" ? "→" : "←"} {fmt(e.amount_usd)} {e.asset_label} · {e.counterparty || ""}
                  </span>
                  <span className="text-stone-500">block {e.block}</span>
                </div>
              ))}
            </div>

            <div className={card}>
              <p className={label}><FileSignature size={12} className="inline mr-2 -mt-0.5" />disbursements — 2-of-2 for the child</p>
              {cov.escrow_address && (
                <div className="space-y-2 mb-4">
                  <div className="flex gap-2 flex-wrap">
                    <input data-testid="proposal-amount" placeholder="Amount USD" value={proposalForm.amount}
                      onChange={(e) => setProposalForm({ ...proposalForm, amount: e.target.value })} className={`${input} w-32`} />
                    <select data-testid="proposal-category" value={proposalForm.category}
                      onChange={(e) => setProposalForm({ ...proposalForm, category: e.target.value })} className={input}>
                      {["EDUCATION", "LAND", "EQUIPMENT", "HEALTH", "GENERAL"].map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                    <input data-testid="proposal-purpose" placeholder="Purpose (books, equipment…)" value={proposalForm.purpose}
                      onChange={(e) => setProposalForm({ ...proposalForm, purpose: e.target.value })} className={`${input} flex-1 min-w-[180px]`} />
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <input data-testid="proposal-recipient" placeholder="vendor wallet 0x…" value={proposalForm.recipient}
                      onChange={(e) => setProposalForm({ ...proposalForm, recipient: e.target.value })} className={`${input} flex-1 min-w-[240px]`} />
                    <button data-testid="proposal-stage-button" disabled={busy}
                      onClick={async () => {
                        const ok = await act("/wallet/covenant/propose", {
                          amount_usd: Number(proposalForm.amount) || 0, category: proposalForm.category,
                          purpose: proposalForm.purpose, recipient_address: proposalForm.recipient });
                        if (ok) { setFlash(`Proposal #${ok.proposal_id} staged — your co-parent's signature releases it.`); setProposalForm({ amount: "", purpose: "", recipient: "", category: "GENERAL" }); }
                      }} className={btn} style={{ background: GOLD }}>STAGE PROPOSAL</button>
                  </div>
                </div>
              )}
              {(view?.proposals || []).map((p) => {
                const mine = p.staged_by === myUuid;
                return (
                  <div key={p.proposal_id} data-testid={`proposal-row-${p.proposal_id}`} className="flex items-center gap-2 flex-wrap font-mono text-[11px] py-2 border-b border-stone-800/60">
                    <span className={p.status === "EXECUTED" ? "text-emerald-400" : "text-amber-400"}>#{p.proposal_id} {p.status}</span>
                    <span className="text-stone-200">{fmt(p.amount_usd)} · {p.category || "GENERAL"} · {p.purpose}</span>
                    <span className="text-stone-500 flex-1">{mine ? "staged by me" : `staged by ${p.staged_by_label || "co-parent"}`}{p.status === "EXECUTED" ? ` · sealed block ${p.countersign_tx?.block}` : ""}</span>
                    {p.status === "PENDING" && !mine && (
                      <button data-testid={`proposal-countersign-${p.proposal_id}`} disabled={busy}
                        onClick={async () => {
                          const ok = await act("/wallet/covenant/countersign", { proposal_id: p.proposal_id });
                          if (ok) setFlash(`Proposal #${p.proposal_id} executed — sealed at block ${ok.block}.`);
                        }} className={`${btn} !px-4 !py-1.5 text-xs`} style={{ background: GOLD }}>COUNTERSIGN</button>
                    )}
                    {p.status === "PENDING" && mine && <span className="text-stone-500 text-[10px]">awaiting co-parent</span>}
                  </div>
                );
              })}
              {(view?.proposals || []).length === 0 && <p className="text-xs text-stone-500">Nothing staged. A proposal moves pool money only when both parents sign.</p>}
            </div>
          </>
        )}

        {(view?.timeline || []).length > 0 && (
          <div className={card}>
            <p className={label}><History size={12} className="inline mr-2 -mt-0.5" />the household story — one chronological wall</p>
            <div data-testid="covenant-timeline">
              {view.timeline.map((e, i) => (
                <div key={i} data-testid="timeline-event" className="relative flex gap-3 items-baseline py-1.5 border-l border-stone-700 pl-4">
                  <span className={`absolute -left-[5px] top-2.5 w-2 h-2 rounded-full ${KIND_COLORS[e.kind] || "bg-stone-500"}`} />
                  <span className="font-mono text-[10px] text-stone-500 w-12 shrink-0">{(e.at || "").slice(5, 10)}</span>
                  <span className="font-mono text-[10px] text-amber-500/80 w-24 shrink-0">{e.kind}</span>
                  <span className="text-xs text-stone-300 flex-1">{e.text}</span>
                  {e.block != null && <span className="font-mono text-[10px] text-stone-600">#{e.block}</span>}
                </div>
              ))}
            </div>
          </div>
        )}

        <div className={card}>
          <p className={label}><History size={12} className="inline mr-2 -mt-0.5" />the contingency record — written at the time, sealed in order</p>
          <p className="text-xs text-stone-500 leading-relaxed mb-3">
            If the covenant is declined, this ledger is the record: what was offered, when, and what
            happened after. Every entry seals against the one before it — it cannot be re-ordered or
            quietly edited later. Offers, declines, settles, deposits, and releases write themselves;
            log the offline moments (drop-offs, calls, expenses) as they happen.
          </p>
          <div className="flex gap-2 flex-wrap mb-2">
            <select data-testid="ledger-kind" value={ledgerForm.kind}
              onChange={(e) => setLedgerForm({ ...ledgerForm, kind: e.target.value })} className={input}>
              {(ledger.kinds.length ? ledger.kinds : ["NOTE", "EXPENSE", "TIME", "COMMUNICATION", "OFFER", "PAYMENT", "INCIDENT"]).map((k) => <option key={k} value={k}>{k}</option>)}
            </select>
            <input data-testid="ledger-amount" type="number" min="0" step="0.01" placeholder="amount (optional)" value={ledgerForm.amount}
              onChange={(e) => setLedgerForm({ ...ledgerForm, amount: e.target.value })} className={`${input} w-32`} />
          </div>
          <div className="flex gap-2 flex-wrap mb-4">
            <input data-testid="ledger-text" placeholder="what happened, in plain words — e.g. she replied she'll review with her attorneys" value={ledgerForm.text}
              onChange={(e) => setLedgerForm({ ...ledgerForm, text: e.target.value })} className={`${input} flex-1 min-w-[220px]`} />
            <button data-testid="ledger-add-button" disabled={busy}
              onClick={async () => {
                const ok = await act("/wallet/court-ledger", {
                  kind: ledgerForm.kind, text: ledgerForm.text,
                  amount_usd: ledgerForm.amount ? parseFloat(ledgerForm.amount) : null });
                if (ok) { setFlash(`Entry sealed — #${ok.entry.seq} · ${ok.entry.entry_hash.slice(0, 12)}…`); setLedgerForm({ kind: "NOTE", text: "", amount: "" }); }
              }} className={btn} style={{ background: GOLD }}>SEAL ENTRY</button>
            <button data-testid="ledger-export-button" onClick={exportLedger}
              className="px-4 py-2 rounded-full text-xs font-semibold border border-stone-600 text-stone-200 hover:border-amber-500/60 transition-colors">
              EXPORT EXHIBIT (PDF)
            </button>
          </div>
          <div data-testid="court-ledger-list">
            {ledger.entries.slice().reverse().map((e) => (
              <div key={e.entry_id} data-testid={`ledger-entry-${e.seq}`} className="py-1.5 border-b border-stone-800/60 font-mono text-[11px]">
                <span className="text-stone-500">#{e.seq} {(e.at || "").slice(5, 16).replace("T", " ")} </span>
                <span className="text-amber-500/80">{e.kind}</span>
                {e.amount_usd != null && <span className="text-stone-300"> ${Number(e.amount_usd).toFixed(2)}</span>}
                {e.block != null && <span className="text-stone-600"> · #{e.block}</span>}
                <span className="text-stone-300"> — {e.text}</span>
                <span className="text-stone-700"> · seal {e.entry_hash.slice(0, 8)}</span>
              </div>
            ))}
            {ledger.entries.length === 0 && <p className="text-xs text-stone-600">No entries yet. The covenant rail writes its own; add the offline moments yourself.</p>}
          </div>
        </div>

        <CovenantCalendar token={token} myUuid={myUuid} />

        <FieldLogCard token={token} />

        <div className={card}>
          <p className={label}><Lock size={12} className="inline mr-2 -mt-0.5" />case document vault — sealed, private, deadline-tracked</p>
          <div className="flex gap-2 flex-wrap mb-3">
            <button data-testid="vault-template-timeshare" disabled={busy} onClick={() => loadTemplate("timeshare50")}
              className="px-4 py-1.5 rounded-full text-xs font-semibold border border-amber-500/50 text-amber-300 hover:bg-amber-500/10 transition-colors">
              50/50 TIMESHARE PROPOSAL
            </button>
            <button data-testid="vault-attach-pdf" disabled={busy} onClick={() => fileRef.current?.click()}
              className="px-4 py-1.5 rounded-full text-xs font-semibold border border-stone-600 text-stone-200 hover:border-amber-500/60 transition-colors">
              {vaultFile ? `PDF: ${vaultFile.name.slice(0, 24)}` : "ATTACH PDF"}
            </button>
            <input data-testid="vault-file-input" ref={fileRef} type="file" accept="application/pdf" className="hidden" onChange={pickFile} />
            {vaultFile && (
              <button data-testid="vault-file-clear" onClick={() => { setVaultFile(null); if (fileRef.current) fileRef.current.value = ""; }}
                className="px-3 py-1.5 rounded-full text-xs text-stone-500 hover:text-red-400 transition-colors">✕</button>
            )}
          </div>
          <div className="space-y-2 mb-4">
            <div className="flex gap-2 flex-wrap">
              <input data-testid="vault-title" placeholder="document title" value={vaultForm.title}
                onChange={(e) => setVaultForm({ ...vaultForm, title: e.target.value })} className={`${input} flex-1 min-w-[180px]`} />
              <select data-testid="vault-category" value={vaultForm.category}
                onChange={(e) => setVaultForm({ ...vaultForm, category: e.target.value })} className={input}>
                {["FILING", "ORDER", "CORRESPONDENCE", "PROPOSAL", "REFERENCE", "OTHER"].map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <input data-testid="vault-deadline" type="date" value={vaultForm.deadline}
                onChange={(e) => setVaultForm({ ...vaultForm, deadline: e.target.value })} className={input} />
            </div>
            <textarea data-testid="vault-text" placeholder={vaultFile ? `attached: ${vaultFile.name} — the PDF seals as-is` : "paste the document text — or load the template above, or attach a PDF"} value={vaultFile ? "" : vaultForm.text}
              onChange={(e) => setVaultForm({ ...vaultForm, text: e.target.value })} rows={5} disabled={Boolean(vaultFile)}
              className={`${input} w-full resize-y disabled:opacity-40`} />
            <div className="flex items-center gap-3 flex-wrap">
              {cov && (
                <label className="flex items-center gap-2 text-[11px] text-stone-400 font-mono">
                  <input data-testid="vault-shared" type="checkbox" checked={vaultForm.shared}
                    onChange={(e) => setVaultForm({ ...vaultForm, shared: e.target.checked })} />
                  shared with co-parent
                </label>
              )}
              <button data-testid="vault-seal-button" disabled={busy}
                onClick={async () => {
                  if (editingDoc) {
                    const ok = await act(`/wallet/vault/documents/${editingDoc}/propose-edit`, { text_body: vaultForm.text });
                    if (ok) { setFlash(`Edit proposed — your co-parent's on-chain signature applies it, then you both sign the new text.`); setVaultForm({ title: "", category: "FILING", deadline: "", text: "", shared: false }); setEditingDoc(null); }
                    return;
                  }
                  const ok = await act("/wallet/vault/documents", {
                    title: vaultForm.title, category: vaultForm.category, deadline: vaultForm.deadline,
                    text_body: vaultFile ? "" : vaultForm.text, content_b64: vaultFile?.b64 || "",
                    shared: vaultForm.shared });
                  if (ok) { setFlash(`Sealed into the vault — ${ok.doc_id} · sha256 ${ok.seal.slice(0, 12)}…`); setVaultForm({ title: "", category: "FILING", deadline: "", text: "", shared: false }); setVaultFile(null); if (fileRef.current) fileRef.current.value = ""; }
                }} className={btn} style={{ background: GOLD }}>{editingDoc ? "PROPOSE EDIT" : "SEAL INTO VAULT"}</button>
              {editingDoc && (
                <button data-testid="vault-edit-cancel" onClick={() => { setEditingDoc(null); setVaultForm({ title: "", category: "FILING", deadline: "", text: "", shared: false }); }}
                  className="px-3 py-1.5 rounded-full text-xs text-stone-500 hover:text-red-400 transition-colors">cancel edit</button>
              )}
              {editingDoc && <span className="font-mono text-[10px] text-amber-400/80">re-drafting {editingDoc} — acceptance resets signatures</span>}
            </div>
          </div>
          {vaultDocs.map((d) => (
            <div key={d.doc_id}>
              <div data-testid={`vault-row-${d.doc_id}`} className="flex items-center gap-2 flex-wrap font-mono text-[11px] py-2 border-b border-stone-800/60">
              <span className="text-stone-200 flex-1 min-w-[160px]">{d.title}</span>
              <span className="text-[10px] text-stone-500 border border-stone-700 rounded-full px-2 py-0.5">{d.category}{d.shared ? " · SHARED" : ""}{!d.mine ? ` · ${d.owner_label || "co-parent"}` : ""}</span>
              {d.days_to_deadline != null && (
                <span data-testid={`vault-deadline-${d.doc_id}`} className={`text-[10px] ${d.days_to_deadline < 0 ? "text-red-400" : d.days_to_deadline <= 7 ? "text-amber-400" : "text-stone-500"}`}>
                  {d.days_to_deadline < 0 ? `overdue ${-d.days_to_deadline}d` : d.days_to_deadline === 0 ? "due today" : `${d.days_to_deadline}d left`}
                </span>
              )}
              <span className="text-stone-600 text-[10px]">seal {d.seal.slice(0, 8)}</span>
              {d.shared && !d.is_pdf && (
                <button data-testid={`vault-edit-${d.doc_id}`} disabled={busy} onClick={() => startEdit(d.doc_id)}
                  title="propose an edit — co-parent signs to apply"
                  className="text-stone-400 hover:text-amber-400 transition-colors"><Pencil size={14} /></button>
              )}
              {d.shared && d.category === "PROPOSAL" && d.proposed_edit && (
                d.proposed_edit.mine ? (
                  <span data-testid={`vault-edit-pending-${d.doc_id}`} className="text-amber-400 text-[10px]">edit pending — awaiting co-parent</span>
                ) : (
                  <button data-testid={`vault-accept-edit-${d.doc_id}`} disabled={busy}
                    onClick={async () => {
                      const ok = await act(`/wallet/vault/documents/${d.doc_id}/countersign`, {});
                      if (ok && ok.edit_applied) setFlash(`Edit applied on-chain — block ${ok.block}. The new text needs both signatures.`);
                    }} className={`${btn} !px-3 !py-1 text-[10px]`} style={{ background: GOLD }}>ACCEPT EDIT ON-CHAIN</button>
                )
              )}
              {d.shared && d.category === "PROPOSAL" && !d.proposed_edit && (
                d.signed_by_me ? (
                  <span data-testid={`vault-signed-${d.doc_id}`} className="text-emerald-400 text-[10px]">
                    you signed{d.signature_status === "COUNTERSIGNED" ? " · COUNTERSIGNED · block " + (d.signature_blocks?.[1] || d.signature_blocks?.[0]) : ""}
                  </span>
                ) : (
                  <button data-testid={`vault-sign-${d.doc_id}`} disabled={busy}
                    onClick={async () => {
                      const ok = await act(`/wallet/vault/documents/${d.doc_id}/countersign`, {});
                      if (ok) setFlash(`Signed on-chain — block ${ok.block}. ${ok.signature_status === "COUNTERSIGNED" ? "Both parents have signed." : "Awaiting your co-parent."}`);
                    }} className={`${btn} !px-3 !py-1 text-[10px]`} style={{ background: GOLD }}>SIGN ON-CHAIN</button>
                )
              )}
              {d.shared && d.category === "PROPOSAL" && d.signed_by_partner && !d.signed_by_me && (
                <span className="text-stone-500 text-[10px]">co-parent signed · block {d.signature_blocks?.[0]}</span>
              )}
              {d.version_count > 0 && (
                <button data-testid={`vault-versions-${d.doc_id}`} onClick={() => toggleVersions(d.doc_id)}
                  className="text-[10px] text-stone-500 hover:text-amber-400 transition-colors border border-stone-700 rounded-full px-2 py-0.5">
                  {d.version_count} prior {d.version_count === 1 ? "version" : "versions"}
                </button>
              )}
              <button data-testid={`vault-download-${d.doc_id}`} onClick={() => downloadDoc(d.doc_id)}
                className="text-stone-400 hover:text-amber-400 transition-colors"><Download size={14} /></button>
              </div>
              {versionsOpen[d.doc_id] && (
                <div data-testid={`vault-versions-list-${d.doc_id}`} className="pl-4 py-1 space-y-1 border-b border-stone-800/60">
                  {versionsOpen[d.doc_id].map((v) => (
                    <div key={v.index} className="flex items-center gap-2 font-mono text-[10px] text-stone-500">
                      <span>v{v.index + 1} · seal {v.seal.slice(0, 8)} · replaced {(v.replaced_at || "").slice(0, 10)} by {v.replaced_by_label}</span>
                      {v.signatures?.length > 0 && <span>· {v.signatures.length} signature{v.signatures.length > 1 ? "s" : ""}</span>}
                      {v.has_text ? (
                        <button data-testid={`vault-version-download-${d.doc_id}-${v.index}`} onClick={() => downloadVersion(d.doc_id, v.index)}
                          className="text-stone-400 hover:text-amber-400 transition-colors"><Download size={12} /></button>
                      ) : <span className="text-stone-700">seal only</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
          {vaultDocs.length === 0 && <p className="text-xs text-stone-500">Nothing sealed yet. Your filings live here — sealed by content hash, visible to no one else unless you mark one shared.</p>}
        </div>

        {flash && <p data-testid="covenant-flash" className="text-emerald-400 text-sm">{flash}</p>}
        {error && <p data-testid="covenant-error" className="text-red-400 text-sm">{error}</p>}
        <p className="text-[9px] text-stone-600 leading-relaxed">
          Decentralized custody: your wallet and card are yours alone — this page never shows your co-parent's
          card, wallet internals, or uuid. The pool's truth is the on-chain escrow balance; it rises only on
          real deposits and moves only on 2-of-2 signatures.
        </p>
      </main>
    </div>
  );
}
